/**
 * Device Detection Utilities
 * Erkennt verschiedene Gerätetypen und Eigenschaften
 */

/**
 * Prüft ob es sich um ein mobiles Gerät handelt
 * @returns {boolean}
 */
export const isMobileDevice = (): boolean => {
  // User-Agent-Erkennung (am zuverlässigsten)
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const mobileRegex = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i;
  
  // Touch-Erkennung
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Screen-Größe (als zusätzliche Prüfung)
  const isSmallScreen = window.innerWidth <= 768;
  
  // Kombiniere alle Methoden (iPadOS meldet sich als „Macintosh“ → eigene Prüfung)
  return mobileRegex.test(userAgent) || isIPadOS() || (hasTouchScreen && isSmallScreen);
};

/**
 * Erkennt ein iPad mit iPadOS 13+. Safari sendet dort standardmäßig den
 * Desktop-User-Agent („Macintosh“), verrät sich aber über Multi-Touch.
 * @returns {boolean}
 */
export const isIPadOS = (): boolean => {
  const ua = window.navigator.userAgent;
  const platform = (window.navigator as any).userAgentData?.platform ?? window.navigator.platform ?? '';
  const looksLikeMac = /Macintosh|MacIntel/i.test(ua) || /^Mac/i.test(platform);
  return looksLikeMac && window.navigator.maxTouchPoints > 1;
};

/**
 * Prüft ob es sich um ein iOS-Gerät handelt
 * @returns {boolean}
 */
export const isIOS = (): boolean => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent) || isIPadOS();
};

/**
 * Prüft ob es sich um ein Android-Gerät handelt
 * @returns {boolean}
 */
export const isAndroid = (): boolean => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /android/.test(userAgent);
};

/**
 * Prüft ob die PWA im Standalone-Modus läuft (installiert)
 * @returns {boolean}
 */
export const isStandalone = (): boolean => {
  // 1. Display-Mode (Standard – Chrome, Edge, Firefox, Safari 17+, TWA)
  const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
  const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
  const isMinimalUi = window.matchMedia('(display-mode: minimal-ui)').matches;

  // 2. iOS/iPadOS Home-Bildschirm-App (Safari-spezifisch)
  const isIOSStandalone = (window.navigator as any).standalone === true;

  // 3. Android: Start aus einer App (TWA/Intent)
  const isAndroidStandalone = document.referrer.includes('android-app://');

  // Bewusst KEINE Fenstergrößen-Heuristik (outerHeight === innerHeight):
  // Safari auf iPad meldet im Browser-Tab gleiche Werte und würde fälschlich
  // als „installiert“ erkannt → falsche Installationsanleitung.
  return isStandaloneMode || isFullscreen || isMinimalUi || isIOSStandalone || isAndroidStandalone;
};

/**
 * Prüft ob die PWA im Browser läuft (nicht installiert)
 * @returns {boolean}
 */
export const isInBrowser = (): boolean => {
  return !isStandalone();
};

/**
 * Prüft ob Touch-Eingabe unterstützt wird
 * @returns {boolean}
 */
export const hasTouchSupport = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * Gibt die Bildschirmbreite zurück
 * @returns {number}
 */
export const getScreenWidth = (): number => {
  return window.innerWidth || document.documentElement.clientWidth || window.screen.width;
};

/**
 * Prüft ob es sich um ein Tablet handelt
 * @returns {boolean}
 */
export const isTablet = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isIPad = /ipad/.test(userAgent) || isIPadOS();
  const isAndroidTablet = /android/.test(userAgent) && !/mobile/.test(userAgent);
  const screenWidth = getScreenWidth();
  
  return isIPad || isAndroidTablet || (screenWidth >= 768 && screenWidth <= 1024 && hasTouchSupport());
};

/**
 * Prüft ob es sich um ein Desktop-Gerät handelt
 * @returns {boolean}
 */
export const isDesktop = (): boolean => {
  return !isMobileDevice() && !isTablet();
};

/**
 * Gibt detaillierte Geräteinformationen zurück
 * @returns {object}
 */
export const getDeviceInfo = () => {
  return {
    isMobile: isMobileDevice(),
    isIOS: isIOS(),
    isIPadOS: isIPadOS(),
    isAndroid: isAndroid(),
    isTablet: isTablet(),
    isDesktop: isDesktop(),
    isStandalone: isStandalone(),
    isInBrowser: isInBrowser(),
    hasTouch: hasTouchSupport(),
    screenWidth: getScreenWidth(),
    userAgent: navigator.userAgent,
    platform: navigator.platform
  };
};

/**
 * Prüft ob die PWA installierbar ist
 * @returns {boolean}
 */
export const isInstallable = (): boolean => {
  // PWA ist installierbar wenn:
  // - Nicht bereits installiert
  // - Mobile-Gerät
  // - HTTPS verwendet wird
  const isHTTPS = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
  
  return isInBrowser() && isMobileDevice() && isHTTPS;
};

export default {
  isMobileDevice,
  isIOS,
  isIPadOS,
  isAndroid,
  isStandalone,
  isInBrowser,
  hasTouchSupport,
  getScreenWidth,
  isTablet,
  isDesktop,
  getDeviceInfo,
  isInstallable
};
